<template>
  <div id="register-container" class="wdgt-container" ref="container">
    <div class="social-logins">
      <button class="btn-social-login btn-google-login">
        <i class="fab fa-google"></i>
        <span class="d-none d-sm-inline d-md-none d-lg-inline ml-1">{{ $t('actions.loginWithGoogle') }}</span>
        <span ref="registerGoogleLoginButton" class="google-login-container" />
      </button>
      <button class="btn-social-login btn-facebook-login" @click="triggerFacebookLogin">
        <i class="fab fa-facebook"></i>
        <span class="d-none d-sm-inline d-md-none d-lg-inline ml-1">{{ $t('actions.loginWithFacebook') }}</span>
      </button>
    </div>
    <div class="mb-3">
      {{ $t('words.registeredCustomer') }} <span class="st-link" @click="$emit('show-login')">{{ $t('actions.login') }}</span>
    </div>
    <VeeForm @submit="register">
      <div class="wdgt-form">
        <div class="form-field">
          <label for="wdgt-register-name">{{ $t('words.name') }}*</label>
          <VeeField id="wdgt-register-name" :modelValue="newCustomer.contactName" @update:modelValue="newCustomer.contactName = $event" name="contactName" type="text" :placeholder="$t('words.name')" rules="required" />
          <ErrorMessage name="contactName" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-register-surname">{{ $t('words.surname') }}*</label>
          <VeeField id="wdgt-register-surname" :modelValue="newCustomer.contactSurname" @update:modelValue="newCustomer.contactSurname = $event" name="contactSurname" type="text" :placeholder="$t('words.surname')" rules="required" />
          <ErrorMessage name="contactSurname" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-register-email">{{ $t('words.email') }}*</label>
          <VeeField id="wdgt-register-email" :modelValue="newCustomer.contactEmail" @update:modelValue="newCustomer.contactEmail = $event" name="contactEmail" type="email" :placeholder="$t('words.email')" rules="required|email" />
          <ErrorMessage name="contactEmail" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-register-password">{{ $t('words.password') }}*</label>
          <VeeField id="wdgt-register-password" :modelValue="newCustomer.password" @update:modelValue="newCustomer.password = $event" name="password" autocomplete="current-password" type="password" :placeholder="$t('words.password')" rules="required" @invalid="onInvalid" />
          <ErrorMessage name="password" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-register-phone">{{ $t('words.phone') }}*</label>
          <PhoneSelector
            v-model="newCustomer.contactPhone1"
            field-id="wdgt-register-phone"
            field-name="contactPhone1"
            :placeholder="$t('words.phone')"
            rules="required|phone"
          />
        </div>
      </div>

      <div class="wdgt-form">
        <div class="form-field checkbox">
          <input id="wdgt-register-invoice" type="checkbox" v-model="generateInvoice">
          <label for="wdgt-register-invoice">
            {{ $t('words.generateInvoice') }}
          </label>
        </div>
      </div>
      <div class="wdgt-form">
        <div v-if="generateInvoice" class="form-field">
          <label for="wdgt-register-vat">{{ $t('words.vat') }}</label>
          <VeeField id="wdgt-register-vat" :modelValue="newCustomer.vat" @update:modelValue="newCustomer.vat = $event" name="vat" type="text" :placeholder="$t('words.vat')" rules="validateCIFNIFNIE" />
          <ErrorMessage name="vat" class="error" />
        </div>
        <div v-if="generateInvoice" class="form-field">
          <label for="wdgt-register-company">{{ $t('words.company') }} ({{ $t('words.optional') }})</label>
          <input id="wdgt-register-company" v-model="newCustomer.companyName" type="text" :placeholder="$t('words.company')" />
        </div>
      </div>
      <div class="wdgt-form mb-3">
        <div class="form-field checkbox">
          <VeeField
            name="acceptPrivacyPolicy"
            type="checkbox"
            :value="true"
            :unchecked-value="false"
            rules="checkbox_required"
            v-model="acceptPrivacyPolicy"
          />
          <label for="wdgt-register-terms">
            {{ $t('words.beforePrivacyPolicy') }} <a href="/politica-de-privacitat/" target="_blank" :title="$t('words.privacyPolicy')">{{ $t('words.privacyPolicy') }}</a>*
          </label>
          <ErrorMessage name="acceptPrivacyPolicy" class="error" />
        </div>
      </div>
      <div class="checkout-buttons">
        <button type="button" class="checkout d-flex" @click="$emit('show-basket')">
          <i class="material-icons-round">shopping_cart</i>
          {{ $t('words.basket') }}
        </button>
        <button type="submit" class="registerButton green d-flex ml-auto" :disabled="disableRegisterButton">
          {{ $t('actions.register') }}
        </button>
      </div>
    </VeeForm>
  </div>
</template>
<script>
import {mapActions, mapGetters} from 'vuex';
import { LoginCredentials } from '@/models/LoginCredentials';
import {Customer} from "@/models/Customer";
import CustomerRepository from "@/api/CustomerRepository";
import {defineRule} from "vee-validate";
import {Form as VeeForm, Field as VeeField, ErrorMessage} from "vee-validate";
import {i18n} from "@/lang";
import Swal from "sweetalert2";
import PhoneSelector from "@/components/common/PhoneSelector.vue";
import { validatePhoneNumber, getPhoneValidationMessage } from "@/utils/phoneValidation";

defineRule('required', (value) => {
  if (value === null || value === undefined || value === false || (typeof value === 'string' && !value.length) || (Array.isArray(value) && !value.length)) {
    return i18n.global.t('forms.required');
  }
  return true;
});

defineRule('checkbox_required', (value) => {
  if (value === true) {
    return true;
  }
  return i18n.global.t('forms.required');
});

defineRule('email', (value) => {
  if (!value || !value.length) {
    return true;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return i18n.global.t('forms.email');
  }
  return true;
});

defineRule('phone', (value) => {
  if (!value || !value.length) {
    return true;
  }
  if (!validatePhoneNumber(value)) {
    return getPhoneValidationMessage(i18n, value);
  }
  return true;
});

defineRule('validateCIFNIFNIE', (value) => {
  if (!value || !value.length) {
    return true;
  }
  if (!value.match(/^[0-9A-Za-z]{9,12}$/)) {
    return i18n.global.t('forms.dninifcif');
  }
  return true;
});

export default {
  components: {VeeForm, VeeField, ErrorMessage, PhoneSelector},
  data() {
    return {
      loginCredentials: new LoginCredentials(),
      newCustomer: new Customer(),
      placeholderPassword: '*********',
      disableRegisterButton: false,
      generateInvoice: false,
      acceptPrivacyPolicy: false,
    }
  },
  computed: {
    ...mapGetters('session', ['customer']),
    ...mapGetters('basket', ['orderDetails']),
  },
  methods: {
    ...mapActions('session', ['login','updateCustomerData']),
    goBack() {
      this.$emit('back')
    },
    getLicense() {
      return window._pg?.license || process.env.VUE_APP_LICENSE;
    },
    onInvalid(ev) {
      ev.target.classList.add('touched')
    },
    async register(values) {
      console.log('Form submitted with values:', values);

      /* merda per no duplicar clicks al boto pagar */
      if (this.disableRegisterButton) {
        return false
      }
      this.disableRegisterButton = true
      setTimeout(() => {
        this.disableRegisterButton = false
      }, 3000);
      /* final de merda  */
      try {
        let response = await CustomerRepository.create(this.newCustomer);
        this.newCustomer.id = response.data.id;
        let loginCredentials = new LoginCredentials();
        loginCredentials.license = this.getLicense();
        loginCredentials.username = this.newCustomer.contactEmail;
        loginCredentials.password = this.newCustomer.password;
        this.login(loginCredentials);

        if (this.orderDetails.length > 0) {
          this.$emit('show-basket');
        } else {
          // Volver al listado de productos
          this.$emit('back');
        }

      } catch (err) {
        if (err.response.data.code === 1000011) {
          await Swal.fire({
            title: i18n.global.t('messages.customerAlreadyExists'),
            text: i18n.global.t('errors.1000011', {email: this.loginCredentials.username}),
            icon: 'error',
            showCancelButton: false
          });
          this.$emit('show-login');
        } else {
          await Swal.fire({
            title: i18n.global.t('messages.sentEmailRecoveryFail'),
            text: i18n.global.t('messages.sentEmailRecoveryFailText'),
            icon: 'error',
            showCancelButton: false
          });
        }
      }
    },
    triggerFacebookLogin() {
      document.getElementById('facebookLoginButton').click()
    },
  },
  mounted() {
    setTimeout(() => {
      window.google.accounts.id.renderButton(this.$refs.registerGoogleLoginButton, { type: 'standard', size: 'large', width: 500 })
    }, 1000);
  }
}
</script>
<style scoped>
button[type="button"],
button[type="submit"],
input[type=checkbox] {
  cursor: pointer;
}

table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dedede;
  border-right: none;
  border-bottom: none;
  border-radius: 5px;
  color: #464958;
  margin: auto auto 15px;
}

thead {
  background-color: #fafafa;
}

th,
td {
  border: 1px solid #dedede;
  border-left: none;
  border-top: none;
  padding: 5px;
  vertical-align: middle;
}

.wdgt-product-image {
  width: 70px;
  text-align: center;
}

.wdgt-product {
  width: 60%;
  text-align: left;
}

.wdgt-quantity {
  text-align: center;
}

.wdgt-price {
  text-align: right;
  white-space: nowrap;
}

#_pg-app input.wdgt-quantity {
  -moz-appearance: textfield;
  appearance: textfield;
  margin-bottom: 0;
  display: inline;
  width: 50px;
}

input.wdgt-quantity::-webkit-inner-spin-button,
input.wdgt-quantity::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

input.touched:valid {
  /*
  border: inherit !important;
  outline: inherit !important;
  */
}

input.touched:invalid {
  border: 1px solid rgb(156, 33, 25) !important;
  outline: 1px solid rgb(156, 33, 25) !important;
  box-shadow: 0 0 0 0 rgba(156, 33, 25, 1);
  transform: scale(1);
  animation: pulse 2s 50ms;
}

/* Pulse effect on error */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(156, 33, 25, 0.8);
  }

  50% {
    transform: scale(0.95);
    box-shadow: 0 0 0 10px rgba(156, 33, 25, 0);
  }

  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(156, 33, 25, 0);
  }
}

@media (max-width: 425px) {
  .wdgt-product {
    width: 50%;
  }

  th,
  td {
    padding: 2px;
  }

  td.quantity button {
    display: none;
  }
}

.wdgt-form {
  display: flex;
  flex-wrap: wrap;
}

.form-field {
  margin-bottom: 15px;
  width: 50%;
  padding-right: 15px;
}

.form-field > label {
  display: block;
  margin-bottom: 5px;
}

.form-field.checkbox > label {
  display: inline;
  margin-left: 5px;
}

.checkout-buttons {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .form-field {
    width: 100%;
  }

  .checkout-buttons {
    flex-direction: column;
    justify-items: center;
    margin-top: 10px;
  }

  .checkout-buttons button {
    width: 100%;
    margin: 0 0 15px 0;
  }
}

@media (max-width: 425px) {
  input {
    width: 100%;
    padding: 5px;
  }
}
.wdgt-price .has-discount{
  text-decoration:line-through;
  color:#333 !important;
  font-weight: normal !important;
}
.wdgt-price-discount{
  color: #a81010;
  white-space: nowrap;
}

button.basket-button,
button.registerButton{
  background-color: #c91919;
  color: white;
  font-size: 20px;
  padding: 10px 40px 10px 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
button.checkout{
  background: #acacac;
  color: white;
  font-size: 20px;
  padding: 10px 40px 10px 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
}
.wdgt-quantity button,
.wdgt-quantity input{
  border: 1px solid #dedede;
  border-radius: 5px;
  padding: 5px 10px;
}
input{
  font-size: 18px;
  padding: 10px;
  margin: 0px 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #dedede;
  width: 100%;
}
textarea{
  font-size: 18px;
  padding: 10px;
  margin: 0px 0;
  border: none;
  border-radius: 4px;
  border: 1px solid #dedede;
  width: 100%;
  resize: vertical;
  font-family: inherit;
  min-height: 60px;
}
.checkbox input{
  width: auto;
}
.checkbox label {
  line-height: 25px;
}
.wdgt-form .checkbox label:before{
  background-color: #fafafa;
  border: 1px solid #dedede;
  border-radius: 4px;
  top: 0;
}
/* Small devices (portrait tablets and large phones, 600px and up) */
@media only screen and (max-width: 768px) {
  .wdgt-quantity button{
    display: none;
  }
  .payment-buttons button,
  .checkout-buttons button{
    width: 100% !important;
    max-width: 100% !important;
    align-items: center;
    justify-content: center;
  }
  .payment-buttons{
    display: block !important;
  }
  .registerButton {
    margin-top: 1rem !important;
  }
  button.checkout{
    margin: 0;
  }
}

.form-field span.error {
  display: block;
  margin-bottom: 0.5em;
  font-size: .75em;
  color: #f22435;
}
button:disabled{
  opacity: 0.6;
  cursor: not-allowed;
}

.social-logins {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}


.btn-social-login {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  color: #000;
  border: 1px solid #000;
  border-radius: 2rem;
  padding: 0.5rem 1rem;
  margin: 0.5rem 0;
  cursor: pointer;
  width: 100%;
}
.btn-google-login {
  background-color: #fff;
  color: #000;
  border: 1px solid #000;
  margin-right: 10px;
}
.btn-facebook-login {
  background-color: #3b5998;
  color: #fff;
  border: 1px solid #3b5998;
}
.btn-microsoft-login {
  background-color: #f25022;
  color: #fff;
  border: 1px solid #f25022;
}
.btn-apple-login {
  background-color: #000;
  color: #fff;
  border: 1px solid #000;
}

.google-login-container{
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.01;
}

/* Country selector styles */
.country-selector {
  position: relative;
  width: 100%;
}

.country-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.country-search {
  width: 100%;
  padding: 10px;
  border: none;
  border-bottom: 1px solid #dedede;
  font-size: 16px;
  outline: none;
}

.country-list {
  max-height: 250px;
  overflow-y: auto;
}

.country-option {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
}

.country-option:hover {
  background-color: #f5f5f5;
}

.country-option:last-child {
  border-bottom: none;
}

/* Responsive adjustments for country selector */
@media (max-width: 768px) {
  .country-dropdown {
    max-height: 200px;
  }

  .country-list {
    max-height: 150px;
  }

  .country-option {
    padding: 8px;
    font-size: 14px;
  }
}

/* How met selector styles */
.how-met-selector {
  position: relative;
  width: 100%;
}

.how-met-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.how-met-list {
  max-height: 250px;
  overflow-y: auto;
}

.how-met-option {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
}

.how-met-option:hover {
  background-color: #f5f5f5;
}

.how-met-option:last-child {
  border-bottom: none;
}

/* Responsive adjustments for how met selector */
@media (max-width: 768px) {
  .how-met-dropdown {
    max-height: 200px;
  }

  .how-met-list {
    max-height: 150px;
  }

  .how-met-option {
    padding: 8px;
    font-size: 14px;
  }
}

/* Form section title styles */
.form-section-title {
  width: 100%;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 2px solid #dedede;
}

.form-section-title h3 {
  margin: 0;
  font-size: 18px;
  color: #464958;
  font-weight: 600;
}

@media (max-width: 768px) {
  .form-section-title h3 {
    font-size: 16px;
  }
}
</style>
